<template>
  <div class="search-filter-section">
    <!-- Header with Total Count -->
    <div class="filter-header">
      <span class="filter-title">
        <a-icon type="filter" />
        {{ $t("actualite.searchAndFilters") }}
      </span>
      <div class="filter-total">
        <span class="total-count">
          <span class="total-badge">{{ totalItems }}</span>
          {{ totalItems === 1 ? $t("actualite.actualiteLabel") : $t("actualite.actualitesPlural") }}
        </span>
        <a-button
          v-if="hasActiveFilters"
          @click="clearFilters"
          size="small"
          icon="close"
          type="primary"
          class="mt-2"
          block
        >
          {{ $t("actualite.clearFilters") }}
        </a-button>
      </div>
    </div>

    <div class="filter-fields">
      <!-- Search by Title -->
      <div class="filter-field">
        <a-input
          v-model="searchFilters.title"
          :placeholder="$t('actualite.searchByTitle')"
          @pressEnter="handleSearch"
          allowClear
        >
          <a-icon slot="prefix" type="search" />
        </a-input>
      </div>

      <!-- Filter by Category -->
      <div class="filter-field">
        <a-select
          v-model="searchFilters.category"
          :placeholder="$t('actualite.filterByCategory')"
          style="width: 100%"
          allowClear
        >
          <a-select-option
            v-for="option in categoryFilterOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </a-select-option>
        </a-select>
      </div>

      <!-- Date Picker -->
      <div class="filter-field">
        <a-date-picker
          v-model="searchFilters.date"
          :placeholder="$t('actualite.filterByCreationDate')"
          style="width: 100%"
          format="DD/MM/YYYY"
        />
      </div>

      <!-- Filter by Target -->
      <div class="filter-field">
        <a-select
          v-model="searchFilters.target"
          :placeholder="$t('actualite.filterByTarget')"
          @change="handleTargetChange"
          style="width: 100%"
          allowClear
        >
          <a-select-option value="all">{{ $t("actualite.tous") }}</a-select-option>
          <a-select-option value="parents">{{ $t("actualite.parents") }}</a-select-option>
          <a-select-option value="students">{{ $t("actualite.students") }}</a-select-option>
          <a-select-option value="teacher">{{ $t("actualite.teachers") }}</a-select-option>
          <a-select-option value="administration">{{ $t("actualite.administration") }}</a-select-option>
        </a-select>
      </div>

      <!-- Filter by Validation Status -->
      <div class="filter-field">
        <a-select
          v-model="searchFilters.validationStatus"
          :placeholder="$t('actualite.filterByValidation')"
          style="width: 100%"
          allowClear
        >
          <a-select-option value="all">{{ $t("actualite.allNews") }}</a-select-option>
          <a-select-option value="pending">{{ $t("actualite.pendingOnly") }}</a-select-option>
          <a-select-option value="validated">{{ $t("actualite.validatedOnly") }}</a-select-option>
          <a-select-option value="rejected">{{ $t("actualite.rejectedOnly") }}</a-select-option>
        </a-select>
      </div>
    </div>

    <!-- Advanced Filters for Parents -->
    <div v-if="searchFilters.target === 'parents'" class="filter-advanced">
      <a-tag color="blue" class="filter-hint-tag">{{ $t("actualite.specifiedInfo") }}</a-tag>

      <div class="filter-field">
        <a-select
          v-model="searchFilters.specificFor"
          :placeholder="$t('actualite.specificFor')"
          @change="handleSpecificForChange"
          style="width: 100%"
        >
          <a-select-option value="all">{{ $t("actualite.tous") }}</a-select-option>
          <a-select-option value="students">{{ $t("actualite.students") }}</a-select-option>
          <a-select-option value="levels">{{ $t("actualite.levels") }}</a-select-option>
          <a-select-option value="classrooms">{{ $t("actualite.classrooms") }}</a-select-option>
          <a-select-option value="activities">{{ $t("actualite.activities") }}</a-select-option>
        </a-select>
      </div>

      <div v-if="searchFilters.specificFor !== 'all'" class="filter-field">
        <LevelsClassroomsFilter
          v-if="searchFilters.specificFor === 'levels'"
          type="levels"
          :value="searchFilters.levels"
          :level-options="levelOptions"
          :classes="classes"
          @input="searchFilters.levels = $event"
        />
        <LevelsClassroomsFilter
          v-else-if="searchFilters.specificFor === 'classrooms'"
          type="classrooms"
          :value="searchFilters.classrooms"
          :level-options="levelOptions"
          :classes="classes"
          @input="searchFilters.classrooms = $event"
        />

        <multiselect
          v-else-if="searchFilters.specificFor === 'students'"
          v-model="selectedStudents"
          :placeholder="$t('sms.selectionnerEleves')"
          :selectLabel="$t('sms.pressEnterToSelect')"
          :options="studentList"
          :custom-label="customLabel"
          :close-on-select="false"
          :preserve-search="true"
          :maxHeight="700"
          label="_id"
          track-by="_id"
          :preselect-first="false"
          :multiple="true"
        >
          <template slot="option" slot-scope="props">
            <div class="option__desc">
              <span class="align-text-top">
                {{ studentOptionTitle(props.option) }}
              </span>
              <br /><span class="option__small font-size-12 text-warning"
                >{{ $t("actualite.parentLabel") }} {{ parentOptionLine(props.option.parent && props.option.parent.father) }}
                {{ $t("all.and") }}
                {{ parentOptionLine(props.option.parent && props.option.parent.mother) }}</span
              >
            </div>
          </template>
        </multiselect>

        <a-select
          v-else-if="searchFilters.specificFor === 'activities'"
          v-model="searchFilters.activities"
          mode="multiple"
          :placeholder="$t('actualite.selectActivities')"
          style="width: 100%"
        >
          <a-select-option
            v-for="activity in activitiesList"
            :key="activity._id"
            :value="activity._id"
          >
            {{ activity.designation }}
          </a-select-option>
        </a-select>
      </div>
    </div>

    <!-- Advanced Filters for Students -->
    <div v-if="searchFilters.target === 'students'" class="filter-advanced">
      <a-tag color="orange" class="filter-hint-tag">{{ $t("actualite.studentSpecifiedInfo") }}</a-tag>

      <div class="filter-field">
        <a-select
          v-model="searchFilters.specificForStudents"
          :placeholder="$t('actualite.specificFor')"
          @change="handleSpecificForStudentsChange"
          style="width: 100%"
        >
          <a-select-option value="all">{{ $t("actualite.tous") }}</a-select-option>
          <a-select-option value="students">{{ $t("actualite.students") }}</a-select-option>
          <a-select-option value="levels">{{ $t("actualite.levels") }}</a-select-option>
          <a-select-option value="classrooms">{{ $t("actualite.classrooms") }}</a-select-option>
          <a-select-option value="activities">{{ $t("actualite.activities") }}</a-select-option>
        </a-select>
      </div>

      <div v-if="searchFilters.specificForStudents !== 'all'" class="filter-field">
        <LevelsClassroomsFilter
          v-if="searchFilters.specificForStudents === 'levels'"
          type="levels"
          :value="searchFilters.levelsStudents"
          :level-options="levelOptions"
          :classes="classes"
          @input="searchFilters.levelsStudents = $event"
        />
        <LevelsClassroomsFilter
          v-else-if="searchFilters.specificForStudents === 'classrooms'"
          type="classrooms"
          :value="searchFilters.classroomsStudents"
          :level-options="levelOptions"
          :classes="classes"
          @input="searchFilters.classroomsStudents = $event"
        />

        <multiselect
          v-else-if="searchFilters.specificForStudents === 'students'"
          v-model="selectedTargetStudents"
          :placeholder="$t('sms.selectionnerEleves')"
          :selectLabel="$t('sms.pressEnterToSelect')"
          :options="studentList"
          :custom-label="customLabel"
          :close-on-select="false"
          :preserve-search="true"
          :maxHeight="700"
          label="_id"
          track-by="_id"
          :preselect-first="false"
          :multiple="true"
        >
          <template slot="option" slot-scope="props">
            <div class="option__desc">
              <span class="align-text-top">
                {{ studentOptionTitle(props.option) }}
              </span>
            </div>
          </template>
        </multiselect>

        <a-select
          v-else-if="searchFilters.specificForStudents === 'activities'"
          v-model="searchFilters.activitiesStudents"
          mode="multiple"
          :placeholder="$t('actualite.selectActivities')"
          style="width: 100%"
        >
          <a-select-option
            v-for="activity in activitiesList"
            :key="activity._id"
            :value="activity._id"
          >
            {{ activity.designation }}
          </a-select-option>
        </a-select>
      </div>
    </div>

    <!-- Advanced Filters for Teachers -->
    <div v-if="searchFilters.target === 'teacher'" class="filter-advanced">
      <a-tag color="green" class="filter-hint-tag">{{ $t("actualite.teacherSpecifiedInfo") }}</a-tag>

      <div class="filter-field">
        <a-select
          v-model="searchFilters.specificForTeachers"
          :placeholder="$t('actualite.specificFor')"
          @change="handleSpecificForTeachersChange"
          style="width: 100%"
        >
          <a-select-option value="all">{{ $t("actualite.tous") }}</a-select-option>
          <a-select-option value="teachers">{{ $t("actualite.teachers") }}</a-select-option>
          <a-select-option value="levels">{{ $t("actualite.levels") }}</a-select-option>
          <a-select-option value="classrooms">{{ $t("actualite.classrooms") }}</a-select-option>
        </a-select>
      </div>

      <div v-if="searchFilters.specificForTeachers !== 'all'" class="filter-field">
        <LevelsClassroomsFilter
          v-if="searchFilters.specificForTeachers === 'levels'"
          type="levels"
          :value="searchFilters.levelsTeachers"
          :level-options="levelOptions"
          :classes="classes"
          @input="searchFilters.levelsTeachers = $event"
        />
        <LevelsClassroomsFilter
          v-else-if="searchFilters.specificForTeachers === 'classrooms'"
          type="classrooms"
          :value="searchFilters.classroomsTeachers"
          :level-options="levelOptions"
          :classes="classes"
          @input="searchFilters.classroomsTeachers = $event"
        />

        <multiselect
          v-else-if="searchFilters.specificForTeachers === 'teachers'"
          v-model="selectedTeachers"
          :placeholder="$t('actualite.selectionnerEnseignants')"
          :selectLabel="$t('sms.pressEnterToSelect')"
          :options="teacherList"
          :custom-label="customTeacherLabel"
          :close-on-select="false"
          :preserve-search="true"
          :maxHeight="700"
          label="_id"
          track-by="_id"
          :preselect-first="false"
          :multiple="true"
        >
        </multiselect>
      </div>
    </div>

    <!-- Search Button -->
    <div class="filter-actions">
      <a-button type="primary" icon="search" block @click="handleSearch">
        {{ $t("actualite.search") }}
      </a-button>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import moment from "moment";
import { mapState } from "vuex";
import { getAllLevels } from "@/config/LevelConstants";
import LevelsClassroomsFilter from "./LevelsClassroomsFilter.vue";

export default {
  name: "ActualityFilters",
  components: { LevelsClassroomsFilter },
  props: {
    studentList: {
      type: Array,
      default: () => [],
    },
    classes: {
      type: Array,
      default: () => [],
    },
    teacherList: {
      type: Array,
      default: () => [],
    },
    activitiesList: {
      type: Array,
      default: () => [],
    },
    totalItems: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      searchFilters: {
        title: null,
        category: undefined,
        date: null,
        target: undefined,
        validationStatus: undefined,
        specificFor: 'all',
        levels: [],
        classrooms: [],
        activities: [],
        specificForStudents: 'all',
        activitiesStudents: [],
        levelsStudents: [],
        classroomsStudents: [],
        specificForTeachers: 'all',
        levelsTeachers: [],
        classroomsTeachers: [],
      },
      selectedStudents: [],
      selectedTargetStudents: [],
      selectedTeachers: [],
      previousTitle: null,
    };
  },
  computed: {
    ...mapState(["settings", "user"]),
    activeBuilding() {
      return this.user.building.find(
        (el) => el.dbName == this.settings.activeBuilding
      );
    },
    countryCode() {
      return this.activeBuilding?.countryCode || "TN";
    },
    allLevels() {
      return getAllLevels(this.$t.bind(this), this.countryCode);
    },
    hasActiveFilters() {
      return (
        this.searchFilters.title ||
        this.searchFilters.category ||
        this.searchFilters.date ||
        this.searchFilters.target ||
        this.searchFilters.validationStatus ||
        this.searchFilters.levels.length > 0 ||
        this.searchFilters.classrooms.length > 0 ||
        this.searchFilters.activities.length > 0 ||
        this.searchFilters.activitiesStudents.length > 0 ||
        this.searchFilters.levelsStudents.length > 0 ||
        this.searchFilters.classroomsStudents.length > 0 ||
        this.selectedTargetStudents.length > 0 ||
        this.searchFilters.levelsTeachers.length > 0 ||
        this.searchFilters.classroomsTeachers.length > 0 ||
        this.selectedStudents.length > 0 ||
        this.selectedTeachers.length > 0
      );
    },
    levelOptions() {
      return Object.entries(this.allLevels).map(([value, label]) => ({
        value,
        label,
      }));
    },
    categoryFilterOptions() {
      return [
        { value: "actualite", label: this.$t("actualite.actualiteLabel") },
        { value: "album", label: this.$t("actualite.album") },
        { value: "info", label: this.$t("actualite.categoryInfo") },
        {
          value: "important_info",
          label: this.$t("actualite.categoryImportantInfo"),
        },
        { value: "urgent", label: this.$t("actualite.categoryUrgent") },
        {
          value: "avis_important",
          label: this.$t("actualite.categoryAvisImportant"),
        },
        { value: "avis", label: this.$t("actualite.categoryAvis") },
        { value: "important", label: this.$t("actualite.categoryImportant") },
      ];
    },
  },
  methods: {
    handleSearch() {
      // Reset to first page when searching
      this.$emit('filter-change', this.getFilterParams());
    },

    performSearch() {
      this.$emit('filter-change', this.getFilterParams());
    },

    clearFilters() {
      this.searchFilters = {
        title: null,
        category: undefined,
        date: null,
        target: undefined,
        validationStatus: undefined,
        specificFor: 'all',
        levels: [],
        classrooms: [],
        activities: [],
        specificForStudents: 'all',
        activitiesStudents: [],
        levelsStudents: [],
        classroomsStudents: [],
        specificForTeachers: 'all',
        levelsTeachers: [],
        classroomsTeachers: [],
      };
      this.selectedStudents = [];
      this.selectedTargetStudents = [];
      this.selectedTeachers = [];
      this.$emit('filter-change', this.getFilterParams());
    },

    handleTargetChange(value) {
      // Reset specific filters when target changes
      this.searchFilters.specificFor = 'all';
      this.searchFilters.specificForStudents = 'all';
      this.searchFilters.specificForTeachers = 'all';
      this.searchFilters.levels = [];
      this.searchFilters.classrooms = [];
      this.searchFilters.activities = [];
      this.searchFilters.activitiesStudents = [];
      this.searchFilters.levelsStudents = [];
      this.searchFilters.classroomsStudents = [];
      this.searchFilters.levelsTeachers = [];
      this.searchFilters.classroomsTeachers = [];
      this.selectedStudents = [];
      this.selectedTargetStudents = [];
      this.selectedTeachers = [];
    },

    handleSpecificForChange(value) {
      // Reset specific filters when specificFor changes
      this.searchFilters.levels = [];
      this.searchFilters.classrooms = [];
      this.searchFilters.activities = [];
      this.selectedStudents = [];
    },

    handleSpecificForStudentsChange(value) {
      // Reset specific filters when specificForStudents changes
      this.searchFilters.activitiesStudents = [];
      this.searchFilters.levelsStudents = [];
      this.searchFilters.classroomsStudents = [];
      this.selectedTargetStudents = [];
    },

    handleSpecificForTeachersChange(value) {
      // Reset specific filters when specificForTeachers changes
      this.searchFilters.levelsTeachers = [];
      this.searchFilters.classroomsTeachers = [];
      this.selectedTeachers = [];
    },

    customLabel({ firstName, lastName, middleName }) {
      const m = middleName && String(middleName).trim();
      return m ? `${firstName} ${m} ${lastName}` : `${firstName} ${lastName}`;
    },

    customTeacherLabel({ employee }) {
      if (!employee) return "";
      const { firstName, lastName, middleName } = employee;
      const m = middleName && String(middleName).trim();
      return m ? `${firstName} ${m} ${lastName}` : `${firstName} ${lastName}`;
    },

    studentOptionTitle(s) {
      if (!s) return "";
      const m = s.middleName && String(s.middleName).trim();
      return m ? `${s.firstName} ${m} ${s.lastName}` : `${s.firstName} ${s.lastName}`;
    },

    parentOptionLine(p) {
      if (!p) return "";
      const m = p.middleName && String(p.middleName).trim();
      return m ? `${p.firstName} ${m} ${p.lastName}` : `${p.firstName} ${p.lastName}`;
    },

    getFilterParams() {
      const params = {};

      // Add search filters if they exist
      if (this.searchFilters.title) {
        params.title = this.searchFilters.title;
      }
      if (this.searchFilters.category) {
        params.category = this.searchFilters.category;
      }
      if (this.searchFilters.date) {
        params.date = moment(this.searchFilters.date).format('YYYY-MM-DD');
      }
      if (this.searchFilters.target) {
        params.target = this.searchFilters.target;
      }
      if (this.searchFilters.validationStatus && this.searchFilters.validationStatus !== 'all') {
        params.validationStatus = this.searchFilters.validationStatus;
      }

      // Add advanced filters for parents
      if (this.searchFilters.target === 'parents' && this.searchFilters.specificFor !== 'all') {
        if (this.searchFilters.specificFor === 'levels' && this.searchFilters.levels.length > 0) {
          params.levels = this.searchFilters.levels.join(',');
        }
        if (this.searchFilters.specificFor === 'classrooms' && this.searchFilters.classrooms.length > 0) {
          params.classrooms = this.searchFilters.classrooms.join(',');
        }
        if (this.searchFilters.specificFor === 'students' && this.selectedStudents.length > 0) {
          params.students = this.selectedStudents.map(s => s._id).join(',');
        }
        if (this.searchFilters.specificFor === 'activities' && this.searchFilters.activities.length > 0) {
          params.activities = this.searchFilters.activities.join(',');
        }
      }

      // Add advanced filters for students
      if (this.searchFilters.target === 'students' && this.searchFilters.specificForStudents !== 'all') {
        if (this.searchFilters.specificForStudents === 'levels' && this.searchFilters.levelsStudents.length > 0) {
          params.levelsStudents = this.searchFilters.levelsStudents.join(',');
        }
        if (this.searchFilters.specificForStudents === 'classrooms' && this.searchFilters.classroomsStudents.length > 0) {
          params.classroomsStudents = this.searchFilters.classroomsStudents.join(',');
        }
        if (this.searchFilters.specificForStudents === 'students' && this.selectedTargetStudents.length > 0) {
          params.targetStudents = this.selectedTargetStudents.map(s => s._id).join(',');
        }
        if (this.searchFilters.specificForStudents === 'activities' && this.searchFilters.activitiesStudents.length > 0) {
          params.activities = this.searchFilters.activitiesStudents.join(',');
        }
      }

      // Add advanced filters for teachers
      if (this.searchFilters.target === 'teacher' && this.searchFilters.specificForTeachers !== 'all') {
        if (this.searchFilters.specificForTeachers === 'levels' && this.searchFilters.levelsTeachers.length > 0) {
          params.levelsTeachers = this.searchFilters.levelsTeachers.join(',');
        }
        if (this.searchFilters.specificForTeachers === 'classrooms' && this.searchFilters.classroomsTeachers.length > 0) {
          params.classroomsTeachers = this.searchFilters.classroomsTeachers.join(',');
        }
        if (this.searchFilters.specificForTeachers === 'teachers' && this.selectedTeachers.length > 0) {
          params.teachers = this.selectedTeachers.map(t => t._id).join(',');
        }
      }

      return params;
    },
  },
};
</script>

<style scoped>
.search-filter-section {
  background: #fff;
  border: 1px solid #e4e6eb;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.03);
}

.filter-header {
  margin-bottom: 14px;
  padding-bottom: 12px;
  border-bottom: 1px solid #eef0f3;
}

.filter-title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 700;
  color: #1c1e21;
  margin-bottom: 8px;
  line-height: 1.25;

  :deep(.anticon) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    font-size: 16px;
  }
}

.filter-total {
  margin-top: 10px;
}

.total-count {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #65676b;
  line-height: 1;
}

.total-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 24px;
  padding: 0 9px;
  border-radius: 20px;
  background: #eef4ff;
  border: 1px solid #c5d8f8;
  color: #4b7cf3;
  font-size: 13px;
  font-weight: 700;
  line-height: 1;
}

.filter-fields {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.filter-field {
  width: 100%;
}

.filter-advanced {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #eef0f3;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.filter-hint-tag {
  margin: 0 !important;
  white-space: normal !important;
  word-wrap: break-word;
  line-height: 1.4;
  max-width: 100%;
}

.filter-actions {
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid #eef0f3;
}

.option__desc {
  padding: 5px;
}
.option__small {
  font-size: 12px;
  color: #999;
}
::v-deep .ant-tag {
  max-width: 100%;
  white-space: normal !important;
  word-wrap: break-word;
  line-height: 1.4;
}
</style>
